import { MerchantType } from "lib/types/merchant";
import {
  CART_SCROLLABLE_ELEMENTS_TYPE,
  CartMetadata,
  CheckoutMetadata,
  DISCOUNT_DISPLAY_TYPE,
  MerchantUIResponseType,
  SSOMetadata,
} from "lib/types/merchantConfigTypes";
import { parseMerchantData, updateCartMetadata, updateCheckoutMetadata } from "lib/utils/merchant";
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { useToken } from "./TokenProvider";
import useSWR, { mutate } from "swr";
import { getRequest, kratosPublicFetcher, staticOptions } from "lib/core/apiClient";
import { Consent, ConsentConfig } from "lib/types/consent";
import { getMarketingConsent, getVisibleConsents } from "lib/utils/consent";
import { appendParamsToItemsUrl } from "lib/utils/checkout";
import { customBranding, injectThemeColors } from "lib/utils/customBranding";
import { ENABLE_APM_LOGS, FLO_MERCHANT_ID } from "lib/utils/constants";
import { initializeLogWrappers } from "lib/core/logUtils";
import { DEFAULT_MERCHANT_STATE } from "lib/constants/merchant-defaults";
import { shouldFetchMerchantApps, shouldFetchUiAttributes } from "lib/utils/kratosPublicFetchGuards";

const Context = createContext<MerchantContext>({ state: {}, actions: {} } as MerchantContext);

export enum ActionType {
  SET_MERCHANT,
  SET_ADDRESS_CONFIG,
  SET_MERCHANT_ATTRIBUTES = "SET_MERCHANT_ATTRIBUTES",
  SET_VALIDATING = "SET_VALIDATING",
  SET_CHECKOUT_METADATA = "SET_CHECKOUT_METADATA",
  SET_CART_METADATA = "SET_CART_METADATA",
  SET_SSO_METADATA = "SET_SSO_METADATA",
  SET_CONSENT_ENABLED = "SET_CONSENT_ENABLED",
  SET_MERCHANT_CONSENTS = "SET_MERCHANT_CONSENTS",
}

export type Action =
  | { type: ActionType.SET_ADDRESS_CONFIG; payload: AddressConfigType[] }
  | {
      type: ActionType.SET_MERCHANT_ATTRIBUTES;
      payload: MerchantType;
    }
  | { type: ActionType.SET_VALIDATING; payload: boolean }
  | { type: ActionType.SET_CONSENT_ENABLED; payload: boolean }
  | { type: ActionType.SET_MERCHANT_CONSENTS; payload: Consent[] }
  | {
      type: ActionType.SET_CHECKOUT_METADATA;
      payload: CheckoutMetadata;
    }
  | {
      type: ActionType.SET_CART_METADATA;
      payload: CartMetadata;
    }
  | {
      type: ActionType.SET_SSO_METADATA;
      payload: SSOMetadata;
    }
  | { type: ActionType.SET_ADDRESS_CONFIG; payload: AddressConfigType[] };

export interface MerchantState {
  isValidating: boolean;
  merchant?: MerchantType;
  checkoutMetadata: CheckoutMetadata;
  cartMetadata: CartMetadata;
  ssoMetadata: SSOMetadata;
  accountId: string | null;
  addressConfig: AddressConfigType[];
  consentState: ConsentState;
}

/** DPDPA consent */
export interface ConsentState {
  /** consent_configuration.consent_enforced on /merchant-apps */
  isConsentEnabled: boolean;
  /** The merchant's consents, without answers. Loaded only when consent is enabled. */
  merchantConsents?: Consent[];
  /** The consents in the login screens' Manage preferences list. Loaded only when consent is enabled. */
  visibleConsents?: Consent[];
  /** MARKETING, which has its own checkbox; null when not offered. Loaded only when consent is enabled. */
  marketingConsent?: Consent | null;
}

export interface AddressConfigType {
  key: string;
  isEnabled: boolean;
  isMandatory: boolean;
  maxCharacterLimit: number;
  minCharacterLimit: number;
}

const merchantInitialState: MerchantState = DEFAULT_MERCHANT_STATE;

export interface MerchantContext {
  state: MerchantState & {
    /** True while /merchant-apps is in flight and the merchant has not been parsed yet */
    isMerchantPending: boolean;
  };
  actions: {
    setAddressConfig: (config: AddressConfigType[]) => void;
  };
}

function reducer(state: MerchantState, action: Action): MerchantState {
  switch (action.type) {
    case ActionType.SET_CONSENT_ENABLED: {
      return {
        ...state,
        consentState: { ...state.consentState, isConsentEnabled: action.payload },
      };
    }
    case ActionType.SET_MERCHANT_CONSENTS: {
      return {
        ...state,
        consentState: {
          ...state.consentState,
          merchantConsents: action.payload,
          visibleConsents: getVisibleConsents(action.payload),
          marketingConsent: getMarketingConsent(action.payload),
        },
      };
    }
    case ActionType.SET_ADDRESS_CONFIG: {
      return {
        ...state,
        addressConfig: action.payload,
      };
    }
    case ActionType.SET_MERCHANT_ATTRIBUTES: {
      return {
        ...state,
        accountId: action.payload?.merchantId,
        merchant: action.payload,
        isValidating: false,
      };
    }

    case ActionType.SET_CHECKOUT_METADATA: {
      return {
        ...state,
        checkoutMetadata: {
          ...(action.payload ?? merchantInitialState.checkoutMetadata),
          metadataLoading: false,
        },
      };
    }

    case ActionType.SET_CART_METADATA: {
      return {
        ...state,
        cartMetadata: {
          ...(action.payload ?? merchantInitialState.checkoutMetadata),
          metadataLoading: false,
        },
      };
    }

    case ActionType.SET_SSO_METADATA: {
      return {
        ...state,
        ssoMetadata: {
          ...(action.payload ?? merchantInitialState.ssoMetadata),
          metadataLoading: false,
        },
      };
    }

    case ActionType.SET_VALIDATING:
      return {
        ...state,
        isValidating: action.payload,
      };

    default: {
      return state;
    }
  }
}

export const MerchantProvider: React.FC<React.PropsWithChildren<{ initialState?: MerchantState }>> = ({
  initialState = merchantInitialState,
  children,
}) => {
  const [reducerState, dispatch] = useReducer(reducer, initialState);
  const [searchParams] = useSearchParams();
  const {
    state: { tokenId, cartPayload, segmentId },
  } = useToken();

  const shopUrl = searchParams.get("shop_url") ?? "";
  const page = searchParams.get("page")?.toLowerCase() ?? "";
  const requestId = searchParams.get("request-id") ?? "";
  const merchantId = searchParams.get("merchantId") ?? searchParams.get("mid") ?? "";
  const fetchMerchantApps = shouldFetchMerchantApps({ tokenId, requestId, shopUrl, merchantId });

  const {
    data: merchantData,
    error,
    isLoading: isMerchantAppsLoading,
  } = useSWR<MerchantUIResponseType>(
    fetchMerchantApps ? "/merchant-apps" : null,
    (url) =>
      kratosPublicFetcher(
        appendParamsToItemsUrl(url, {
          tokenId: tokenId ?? "",
          requestId,
          shopUrl,
          merchantId,
        }),
      ),
    staticOptions,
  );

  // Determine context based on page
  const getContext = () => {
    if (["cart", "empty-cart"].includes(page)) return "CART";
    if (["marketing-popup", "account"].includes(page)) return "SSO";
    if (!page) return "CHECKOUT";
    return null;
  };

  const context = getContext();
  const { segment_id } =
    cartPayload && context !== "CART" ? cartPayload : segmentId ? { segment_id: segmentId } : {};
  const fetchUiAttributes = shouldFetchUiAttributes({
    context,
    tokenId,
    shopUrl,
    segmentId: segment_id,
    requestId,
    merchantId,
  });

  const { data: uiAttributes, error: uiAttributesError } = useSWR<
    CartMetadata | CheckoutMetadata | { ui_attributes: SSOMetadata }
  >(
    fetchUiAttributes ? "/attributes/ui-attributes" : null,
    (url) => {
      return kratosPublicFetcher(
        appendParamsToItemsUrl(url, {
          tokenId: tokenId ?? "",
          context,
          shopUrl,
          segmentId: segment_id,
          requestId,
          merchantId,
        }),
      );
    },
    staticOptions,
  );

  useEffect(() => {
    if (fetchUiAttributes && segment_id && context) {
      mutate(`/attributes/ui-attributes`);
    }
  }, [segment_id, context, fetchUiAttributes]);

  useEffect(() => {
    if (merchantData) {
      const parsedData = parseMerchantData(merchantData, context ?? undefined);
      dispatch({ type: ActionType.SET_MERCHANT_ATTRIBUTES, payload: parsedData });
      dispatch({
        type: ActionType.SET_CONSENT_ENABLED,
        payload: Boolean((merchantData as any)?.consent_configuration?.consent_enforced),
      });
      window.analyticsEndpoint = merchantData?.attributes?.spark_endpoint;
      if (parsedData.merchantId) {
        sessionStorage.setItem(FLO_MERCHANT_ID, parsedData.merchantId);
      }
      if (parsedData.enableLogging) {
        initializeLogWrappers();
      }
    }
  }, [merchantData, context]);

  useEffect(() => {
    if (context === "SSO" && uiAttributes) {
      const uiAttributesSSO = (uiAttributes as { ui_attributes: SSOMetadata }).ui_attributes;
      customBranding(
        uiAttributesSSO.popup.primary_cta.background_color,
        uiAttributesSSO.popup.primary_cta.color,
      );
      return;
    }
    if (merchantData && context !== "SSO") {
      const parsedData = parseMerchantData(merchantData, context ?? undefined);
      customBranding(parsedData.colorPallet?.primaryColor, parsedData.colorPallet?.primaryTextColor);
    }
  }, [uiAttributes, context, merchantData]);

  useEffect(() => {
    if ((!uiAttributes && !uiAttributesError) || !context) return;

    switch (context) {
      case "CART": {
        const cartMetadata = updateCartMetadata(uiAttributes as any);
        injectThemeColors({
          "flo-primary-button-color": cartMetadata.primaryCTAConfig?.backgroundColor,
          "flo-primary-button-text-color": cartMetadata.primaryCTAConfig?.textColor,
        });
        dispatch({
          type: ActionType.SET_CART_METADATA,
          payload: {
            metadataLoading: false,
            ...cartMetadata,
          },
        });
        break;
      }
      case "CHECKOUT":
        dispatch({
          type: ActionType.SET_CHECKOUT_METADATA,
          payload: {
            metadataLoading: false,
            ...updateCheckoutMetadata(uiAttributes as any),
          },
        });
        break;
      case "SSO":
        dispatch({
          type: ActionType.SET_SSO_METADATA,
          payload: (uiAttributes as { ui_attributes: SSOMetadata })?.ui_attributes,
        });
        break;
    }
  }, [uiAttributes, uiAttributesError, context]);

  const setAddressConfig = useCallback((config: AddressConfigType[]) => {
    dispatch({
      type: ActionType.SET_ADDRESS_CONFIG,
      payload: config,
    });
  }, []);

  const actions = useMemo(
    () => ({
      setAddressConfig,
    }),
    [setAddressConfig],
  );

  // DPDPA: the merchant's consents, only fetched when consent is enabled on merchant-apps.
  const consentMerchantId = reducerState.merchant?.merchantId ?? "";
  const { data: consentConfig } = useSWR<ConsentConfig>(
    reducerState.consentState.isConsentEnabled && consentMerchantId
      ? `/public/api/v1/consents/merchant/${consentMerchantId}`
      : null,
    (url: string) => getRequest(url, "AUTH_PUBLIC"),
    staticOptions,
  );

  useEffect(() => {
    if (!consentConfig) return;
    dispatch({ type: ActionType.SET_MERCHANT_CONSENTS, payload: consentConfig.consents ?? [] });
  }, [consentConfig]);

  // Pending until the response is parsed; an empty or failed response is not pending
  const isMerchantPending =
    fetchMerchantApps && (isMerchantAppsLoading || (Boolean(merchantData) && !reducerState.merchant));
  const state = useMemo(() => ({ ...reducerState, isMerchantPending }), [reducerState, isMerchantPending]);

  return (
    <Context.Provider
      value={{
        state,
        actions,
      }}>
      {children}
    </Context.Provider>
  );
};

export function useMerchantContext() {
  if (!Boolean(Context)) throw new Error("useMerchantContext must be used within a MerchantProvider");
  const merchantContext = useContext(Context);
  return merchantContext as MerchantContext;
}
